import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useObraAvance } from '@/hooks/useReportes';
import { formatearCRC } from '@adelante/shared';
import { CircleNotch } from '@phosphor-icons/react';

function fmtNum(n: number): string {
  return n.toLocaleString('es-CR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

/**
 * Detalle de una obra: las sub-partidas que avanzaron esta semana, con su % de
 * la semana, m² y ₡ producidos. Se abre desde la fila de la tabla de Producción.
 */
export function ObraAvanceDialog({
  semana,
  obra,
  onClose,
}: {
  semana: number | null;
  obra: string | null;
  onClose: () => void;
}) {
  const { data, isLoading, isError } = useObraAvance(semana, obra);
  const d = data?.data;

  return (
    <Dialog open={obra !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[760px]">
        <DialogHeader>
          <DialogTitle>Avance de la semana · {obra}</DialogTitle>
          <DialogDescription>
            Sub-partidas que produjeron el avance económico y de m² de esta semana.
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
            <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
            Cargando detalle…
          </div>
        )}
        {isError && <p className="py-8 text-destructive">No se pudo cargar el detalle.</p>}

        {d && !isLoading && (
          <>
            {!d.base_semanal && (
              <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs text-amber-700">
                ⚠️ Sin línea base ni cierre previo: el % de la semana no es fiable.
              </p>
            )}
            {d.subs.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                Esta obra no tuvo avance de sub-partidas en la semana.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Partida · Sub-partida</TableHead>
                    <TableHead className="text-right">% Sem.</TableHead>
                    <TableHead className="text-right">m² Sem.</TableHead>
                    <TableHead className="text-right">₡ Sem.</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {d.subs.map((s) => (
                    <TableRow key={s.sub_id}>
                      <TableCell>
                        <div className="text-sm">
                          <span className="font-mono text-[11px] text-muted-foreground">
                            {s.codigo}
                          </span>{' '}
                          {s.nombre}
                          <span className="ml-1 text-[11px] text-muted-foreground">
                            · S{s.sprint_numero}
                          </span>
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                          {s.partida_codigo} — {s.partida_nombre}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono tabular-nums text-sm text-blue-700">
                        {fmtNum(s.pct_semana)}%
                      </TableCell>
                      <TableCell className="text-right font-mono tabular-nums text-sm">
                        {fmtNum(s.m2_semana)}
                      </TableCell>
                      <TableCell className="text-right font-mono tabular-nums text-sm text-adelante-primary-dark">
                        {formatearCRC(s.monto_semana)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow className="border-t-2">
                    <TableCell className="font-semibold">
                      TOTAL · {d.subs.length} sub-partidas
                    </TableCell>
                    <TableCell />
                    <TableCell className="text-right font-mono tabular-nums text-sm font-semibold">
                      {fmtNum(d.total_m2_semana)}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-sm font-semibold text-adelante-primary-dark">
                      {formatearCRC(d.total_monto_semana)}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
